import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import styles from '@components/CareProfile.module.css';
import BaseErrors from '@components/fields/BaseErrors';
import BooleanRadioButtonGroup from '@components/fields/BooleanRadioButtonGroup';
import CheckboxGroup from '@components/fields/CheckboxGroup';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import RadioButtonGroup from '@components/fields/RadioButtonGroup';
import Form from '@components/forms/Form';
import {
  CareProfileFragment as CareProfile,
  CareProfileDementiaSeverityEnum,
  CareProfileMedicalConditionEnum,
  CareProfileMedicationAssistanceNeedEnum,
  CareProfileMedicationsAffordableEnum,
  CareProfilePrimaryCareProviderStatusEnum,
  InitialConsultReportUpdateDocument,
  InitialConsultReportUpdateMutation,
  InitialConsultReportUpdateMutationVariables
} from '@typing/Generated';
import { InitialConsultFields } from '@typing/Models';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForBoolean } from '@utils/optionUtils';

type FormData = {
  dementiaSeverity: CareProfileDementiaSeverityEnum | null;
  id: string;
  medicalConditions: CareProfileMedicalConditionEnum[];
  medicalConditionsOther: string | null;
  medicationAssistanceNeeds: CareProfileMedicationAssistanceNeedEnum[];
  medicationsAffordable: CareProfileMedicationsAffordableEnum | null;
  primaryCareProviderStatus: CareProfilePrimaryCareProviderStatusEnum | null;
  recentHospitalization: boolean | null;
  recentHospitalizationSynopsis: string | null;
};

type Props = {
  careProfile: CareProfile;
  consultId: string | undefined | null;
  initialConsultReportId: string;
  onCancel?: () => void;
  onSuccess: (nextPage?: string) => void;
  questions?: InitialConsultFields[];
};

const InitialConsultMedicalForm = ({
  careProfile,
  consultId,
  initialConsultReportId,
  onCancel,
  onSuccess,
  questions
}: Props) => {
  const [submitted, setSubmitted] = useState(false);
  const [backOrNext, setBackOrNext] = useState<boolean | null>(null);

  useEffect(() => {
    if (backOrNext !== null && submitted) {
      if (backOrNext) {
        onSuccess('functional');
      } else {
        onSuccess('basic-info');
      }
      setSubmitted(false);
      setBackOrNext(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backOrNext, submitted]);

  const initialValues = {
    dementiaSeverity: careProfile?.dementiaSeverity ?? null,
    medicalConditions: careProfile.medicalConditions?.medicalConditionsOptions ?? [],
    medicalConditionsOther: careProfile.medicalConditions?.other ?? null,
    medicationAssistanceNeeds: careProfile.medicationAssistanceNeeds ?? [],
    medicationsAffordable: careProfile.medicationsAffordable ?? null,
    primaryCareProviderStatus: careProfile.primaryCareProviderStatus ?? null,
    recentHospitalization: careProfile.recentHospitalization ?? null,
    recentHospitalizationSynopsis: careProfile.recentHospitalizationSynopsis ?? null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateInitialConsultReport] = useMutation<
    InitialConsultReportUpdateMutation,
    InitialConsultReportUpdateMutationVariables
  >(InitialConsultReportUpdateDocument);

  const schema = yup
    .object()
    .shape({
      dementiaSeverity: yup.string().nullable(),
      medicalConditions: yup.array(),
      medicalConditionsOther: yup.string().nullable(),
      medicationAssistanceNeeds: yup.array(),
      medicationsAffordable: yup.string().nullable(),
      primaryCareProviderStatus: yup.string().nullable(),
      recentHospitalization: yup.boolean().nullable(),
      recentHospitalizationSynopsis: yup.string().nullable()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const medicalConditions = form.watch('medicalConditions');
  const recentHospitalization = form.watch('recentHospitalization');

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateInitialConsultReport({
      variables: {
        ...form.getValues(),
        consultId,
        id: initialConsultReportId,
        medicalConditions: {
          medicalConditionOptions: form.getValues().medicalConditions,
          other: form.getValues().medicalConditionsOther
        },
        recentHospitalizationSynopsis:
          recentHospitalization === true ? form.getValues().recentHospitalizationSynopsis : null
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.initialConsultReportUpdate,
        intl,
        onSuccess: () => {
          if (!questions) {
            setSubmitted(true);
          } else {
            onSuccess();
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [
    consultId,
    form,
    initialConsultReportId,
    intl,
    onSuccess,
    questions,
    recentHospitalization,
    updateInitialConsultReport
  ]);

  const booleanOptions = useMemo(() => optionsForBoolean(intl), [intl]);

  const medicalConditionOptions = useMemo(() => {
    const options = [
      { option: CareProfileMedicalConditionEnum.heart_condition },
      { option: CareProfileMedicalConditionEnum.cancer },
      { option: CareProfileMedicalConditionEnum.lung_condition },
      { option: CareProfileMedicalConditionEnum.stroke },
      { option: CareProfileMedicalConditionEnum.diabetes },
      {
        help: intl.formatMessage({ id: 'models.pme.questions.medical_conditions.options.dementia.help' }),
        option: CareProfileMedicalConditionEnum.dementia
      },
      { option: CareProfileMedicalConditionEnum.kidney_condition },
      { option: CareProfileMedicalConditionEnum.hearing_or_vision_loss },
      {
        help: intl.formatMessage({ id: 'models.pme.questions.medical_conditions.options.mental_health.help' }),
        option: CareProfileMedicalConditionEnum.mental_health
      },
      { option: CareProfileMedicalConditionEnum.im_not_sure },
      { option: CareProfileMedicalConditionEnum.none }
    ];
    return options.map(option => ({
      help: option.help,
      label: intl.formatMessage({
        id: `models.pme.questions.medical_conditions.options.${option.option}.content`
      }),
      value: option.option
    }));
  }, [intl]);

  const medicationAssistanceNeedsOptions = useMemo(() => {
    const options = [
      CareProfileMedicationAssistanceNeedEnum.refilling,
      CareProfileMedicationAssistanceNeedEnum.organizing_and_dispensing,
      CareProfileMedicationAssistanceNeedEnum.no,
      CareProfileMedicationAssistanceNeedEnum.i_dont_know
    ];

    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.careProfile.questions.medication_assistance_needs.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  const primaryCareProviderStatusOptions = useMemo(() => {
    const options = [
      CareProfilePrimaryCareProviderStatusEnum.has_with_past_year_appointment,
      CareProfilePrimaryCareProviderStatusEnum.has_but_no_past_year_appointment,
      CareProfilePrimaryCareProviderStatusEnum.no,
      CareProfilePrimaryCareProviderStatusEnum.i_dont_know
    ];

    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.careProfile.questions.primary_care_provider_status.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  const medicationsAffordableOptions = useMemo(() => {
    const options = [
      CareProfileMedicationsAffordableEnum.yes,
      CareProfileMedicationsAffordableEnum.no,
      CareProfileMedicationsAffordableEnum.i_dont_know
    ];

    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.careProfile.questions.medications_affordable.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  const dementiaSeverityOptions = useMemo(() => {
    const options = [
      CareProfileDementiaSeverityEnum.mild,
      CareProfileDementiaSeverityEnum.moderate,
      CareProfileDementiaSeverityEnum.severe
    ];
    return options.map(option => ({
      label: intl.formatMessage({
        id: `models.initialConsultReport.questions.dementia_severity.options.${option}.content`
      }),
      value: option
    }));
  }, [intl]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div
        className={
          !questions || questions?.includes(InitialConsultFields.RECENT_HOSPITALIZATION) ? undefined : 'ion-hide'
        }
      >
        <BooleanRadioButtonGroup
          allowDeselection
          control={form.control}
          error={form.formState.errors.recentHospitalization}
          inTracker={false}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.recent_hospitalization.content' })}
          name="recentHospitalization"
          options={booleanOptions}
        />
        <div className={recentHospitalization === true ? undefined : 'ion-hide'}>
          <Input
            control={form.control}
            error={form.formState.errors.recentHospitalizationSynopsis}
            label={intl.formatMessage({ id: 'models.initialConsultReport.questions.recent_hospitalization.synopsis' })}
            name="recentHospitalizationSynopsis"
            spellcheck
          />
        </div>
      </div>
      <div
        className={
          !questions || questions?.includes(InitialConsultFields.MEDICATION_ASSISTANCE_NEEDS) ? undefined : 'ion-hide'
        }
      >
        <CheckboxGroup
          control={form.control}
          errors={form.formState.errors.medicationAssistanceNeeds}
          label={intl.formatMessage({ id: 'models.careProfile.questions.medication_assistance_needs.content' })}
          name="medicationAssistanceNeeds"
          options={medicationAssistanceNeedsOptions}
          smallerLabel
        />
      </div>
      <div
        className={
          !questions || questions?.includes(InitialConsultFields.MEDICATIONS_AFFORDABLE) ? undefined : 'ion-hide'
        }
      >
        <RadioButtonGroup<FormData>
          allowDeselection
          control={form.control}
          error={form.formState.errors.medicationsAffordable}
          label={intl.formatMessage({ id: 'models.careProfile.questions.medications_affordable.content' })}
          name="medicationsAffordable"
          options={medicationsAffordableOptions}
        />
      </div>
      <div className={!questions ? undefined : 'ion-hide'}>
        <CheckboxGroup<FormData>
          control={form.control}
          errors={form.formState.errors.medicalConditions}
          label={intl.formatMessage({ id: 'models.pme.questions.medical_conditions.content' })}
          name="medicalConditions"
          options={medicalConditionOptions}
          smallerLabel
        />
        <div className={styles.otherInputWrapper}>
          <Input<FormData>
            control={form.control}
            error={form.formState.errors.medicalConditionsOther}
            isOption
            name="medicalConditionsOther"
            placeholder={intl.formatMessage({
              id: 'models.pme.questions.medical_conditions.options.other.content'
            })}
            spellcheck
          />
        </div>
      </div>
      <div
        className={!questions || questions?.includes(InitialConsultFields.DEMENTIA_SEVERITY) ? undefined : 'ion-hide'}
      >
        <div className={medicalConditions.includes(CareProfileMedicalConditionEnum.dementia) ? undefined : 'ion-hide'}>
          <RadioButtonGroup<FormData>
            allowDeselection
            control={form.control}
            error={form.formState.errors.dementiaSeverity}
            label={intl.formatMessage({ id: 'models.initialConsultReport.questions.dementia_severity.content' })}
            name="dementiaSeverity"
            options={dementiaSeverityOptions}
          />
        </div>
      </div>
      <div
        className={
          !questions || questions?.includes(InitialConsultFields.PRIMARY_CARE_PROVIDER_STATUS) ? undefined : 'ion-hide'
        }
      >
        <RadioButtonGroup<FormData>
          allowDeselection
          control={form.control}
          error={form.formState.errors.primaryCareProviderStatus}
          label={intl.formatMessage({ id: 'models.careProfile.questions.primary_care_provider_status.content' })}
          name="primaryCareProviderStatus"
          options={primaryCareProviderStatusOptions}
        />
      </div>
      {!questions ? (
        <div className={styles.spaceBetween}>
          <SecondaryButton
            data-test="medical-form-previous"
            disabled={!form?.formState.isValid || form.formState.isSubmitting}
            onClick={() => setBackOrNext(false)}
            textKey="dictionary.previous"
            type="submit"
          />
          <PrimaryButton
            data-test="submit-button"
            disabled={!form?.formState.isValid || form.formState.isSubmitting}
            onClick={() => setBackOrNext(true)}
            textKey="dictionary.next"
            type="submit"
          />
        </div>
      ) : (
        <FormButtons hookForm={form} onCancel={onCancel} />
      )}
    </Form>
  );
};

export default InitialConsultMedicalForm;
